import React, { useMemo, useState, useEffect } from 'react'
import { useConfirm, confirmDelete, confirmLogout } from '../../../components/ConfirmDialog';
import Table from './../../../components/Table'  // new
import { connect } from 'react-redux'
import Modal from '../../../components/Modal';
import { getCellsRequested, addCellRequested, updateCellRequested, deleteCellRequested } from "../../../actions/cellAction";
import { getAssignableLeadersRequested } from "../../../actions/cellLeaderAction";
import PropTypes from 'prop-types';
import { TrashIcon, PlusCircleIcon, UserGroupIcon, HomeIcon, DownloadIcon, MapIcon, TableIcon } from '@heroicons/react/outline';
import CellMap from '../../../components/CellMap';
import { exportToExcel, CELL_EXPORT_COLUMNS } from '../../../utils/export/excel';
import API from '../../../utils/api';
import moment from 'moment';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { bindActionCreators } from 'redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SearchableSelect from '../../../components/SearchableSelect';

const Cell = ({
    user,
    cellsGetRequested,
    cells,
    getCellsRequested: getCell,
    addCellRequested: addCell,
    updateCellRequested: updateCell,
    deleteCellRequested: deleteCell,
    getAssignableLeadersRequested: getAssignableLeaders,
    assignableLeaders }) => {
    const confirm = useConfirm();
    const [showModal, setShowModal] = React.useState(false);
    const [edit, setEdit] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [formData, setFormData] = React.useState({});
    // Filters and view live in the URL (?city=…&type=…&view=map) so they
    // survive a refresh, work with Back, and can be shared as a link.
    const [searchParams, setSearchParams] = useSearchParams();
    const cityFilter = searchParams.get('city') || '';
    // Several cell types can be selected at once (?type=Home+Cell&type=Family+Cell).
    const typeFilters = searchParams.getAll('type');
    const view = searchParams.get('view') === 'map' ? 'map' : 'table';
    const setParam = (key, value) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (value) next.set(key, value); else next.delete(key);
            return next;
        }, { replace: key === 'view' });
    };
    const setCityFilter = (value) => setParam('city', value);
    const setTypeFilters = (types) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('type');
            types.forEach((t) => next.append('type', t));
            return next;
        });
    };
    const setView = (value) => setParam('view', value === 'map' ? 'map' : '');
    const [geocoding, setGeocoding] = React.useState(false);
    const navigate = useNavigate();

    const cities = useMemo(() => {
        const unique = new Set(cells.map(c => (c.city || '').trim()).filter(Boolean));
        return [...unique].sort((a, b) => a.localeCompare(b));
    }, [cells]);
    const hasCellsWithoutCity = cells.some(c => !(c.city || '').trim());

    // City filter first: the stat cards count within the chosen city.
    const cityCells = useMemo(() => {
        if (!cityFilter) return cells;
        if (cityFilter === '__none__') return cells.filter(c => !(c.city || '').trim());
        return cells.filter(c => (c.city || '').trim() === cityFilter);
    }, [cells, cityFilter]);

    // Then the cell type chosen by clicking a stat card.
    const filteredCells = useMemo(() => {
        if (typeFilters.length === 0) return cityCells;
        return cityCells.filter(c => typeFilters.includes(c.cell_type));
    }, [cityCells, typeFilters.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!cellsGetRequested) {
            getCell()
        }
    }, [cellsGetRequested]);

    // Cell leaders, admins and super admins can all lead a cell.
    useEffect(() => {
        getAssignableLeaders();
    }, [getAssignableLeaders]);

    const leaderOptions = useMemo(() => (assignableLeaders || []).map(person => {
        const roles = person.roles || [];
        const tag = roles.includes(3) ? ' (Super admin)' : roles.includes(2) ? ' (Admin)' : '';
        return { value: person.id, label: `${person.name}${tag}` };
    }), [assignableLeaders]);

    const columns = React.useMemo(() => [
        {
            Header: "#",
            Cell: ({ cell }) => (
                <div className="flex items-center space-x-4">
                    <p>{cell.row.index + 1}</p>
                </div>
            ),
        },
        {
            Header: "Name",
            accessor: 'name',
        },
        {
            Header: "Address",
            accessor: 'address',
        },
        {
            Header: "City",
            accessor: 'city',
            Cell: ({ value }) => <div className="text-sm text-gray-500">{value || '—'}</div>,
        },
        {
            Header: "Cell Leader",
            accessor: 'CellLeader.name',
        },
        {
            Header: "Meeting Day",
            accessor: 'meeting_day',
            // Cell: StatusPill,
        },
        {
            Header: "Meeting Time",
            accessor: 'meeting_time',
            // Cell: StatusPill,
        },
        {
            Header: "Action",
            accessor: 'id',
            Cell: ({ cell }) => (
                <div className="flex items-center space-x-4">
                    <button
                        onClick={() => handleEditCell(cell.row.original)}
                        className="px-3 py-1 uppercase leading-wide font-bold text-xs rounded-full shadow-sm bg-yellow-100 text-yellow-800"
                    >Edit</button>
                    <button
                        onClick={() => handleDeleteCell(cell.row.original)}
                        className="px-3 py-1 uppercase leading-wide font-bold text-xs rounded-full shadow-sm bg-red-100 text-red-800"
                    ><TrashIcon className="h-5 w-5" /></button>
                </div>
            ),
        }
    ], [])

    if (!user || !user.id) {
        navigate('/login');
        return;
    }

    const notify = (msg) => toast.success(msg, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: undefined,
        theme: "light",
    });

    const handleAddCell = () => {
        console.log("Add cell");
        setShowModal(true);
    }

    const handleEditCell = (data) => {
        console.log("Edit cell");
        setEdit(true);
        setFormData(data);
        setShowModal(true);
    }

    const handleDeleteCell = async (cell) => {
        const ok = await confirmDelete(confirm, {
            what: 'cell', name: cell.name,
            consequence: "Its members, guests and reports will no longer be shown. This can't be undone.",
        });
        if (!ok) return;
        notify("Deleting Cell...");
        deleteCell(cell.id);
    }

    const handleCloseModal = (e) => {
        e.preventDefault();
        setFormData({});
        setEdit(false);
        setShowModal(false);
    };

    const handleSaveCell = (e) => {
        e.preventDefault();
        console.log("Save Cell");
        console.log(formData);
        addCell(formData);
        notify("Cell created successfully!");
        setShowModal(false);
    };

    const handleUpdateCell = (e) => {
        e.preventDefault();
        console.log("Edit cell");
        console.log(formData);
        updateCell(formData);
        setShowModal(false);
        setEdit(false);
    }

    const handleChange = (event) => {
        const name = event.target.name;
        const value = event.target.value;
        setFormData(values => ({ ...values, [name]: value }))
    }

    // console.log(`Member data ${cellMembers}`)
    // const data = React.useMemo(() => cellMembers, [])

    const handleExport = () => {
        const suffix = [cityFilter !== '__none__' && cityFilter, ...typeFilters]
            .filter(Boolean).map(v => `_${v.replace(/\s+/g, '-')}`).join('');
        exportToExcel(filteredCells, CELL_EXPORT_COLUMNS, `cells${suffix}_${moment().format('YYYY-MM-DD')}`, 'Cells');
    };

    const handleGeocodeMissing = async () => {
        setGeocoding(true);
        try {
            const result = await API.postData('cells/geocode-missing', {});
            if (result && typeof result.geocoded === 'number') {
                const failedCount = result.failed?.length || 0;
                const approxCount = result.approximate?.length || 0;
                const parts = [`Located ${result.geocoded} cell(s).`];
                if (approxCount) parts.push(`${approxCount} placed at their city centre until the address is updated.`);
                if (failedCount) parts.push(`${failedCount} could not be found (no usable address or city).`);
                toast.info(parts.join(' '));
                getCell();
            } else {
                toast.error('Could not locate cells. Please try again.');
            }
        } finally {
            setGeocoding(false);
        }
    };

    // Stats follow the city filter but not the type filter, so every card
    // keeps showing its own count while one of them is selected.
    const totalCells = cityCells.length;
    const homeCells = cityCells.filter(c => c.cell_type === 'Home Cell').length;
    const campusCells = cityCells.filter(c => c.cell_type === 'Campus Cell').length;
    const familyCells = cityCells.filter(c => c.cell_type === 'Family Cell').length;
    // Each type card adds or removes its type; Total Cells clears the selection.
    const toggleType = (type) => setTypeFilters(
        typeFilters.includes(type) ? typeFilters.filter(t => t !== type) : [...typeFilters, type]
    );

    return (
        <div className="min-h-screen bg-gray-100 text-gray-900">
            <Modal
                title={edit ? 'Edit cell' : 'Create a new cell'}
                description={edit ? 'Update where and when this cell meets, and who leads it.' : 'Add a cell group and choose who leads it.'}
                size="lg"
                submitLabel={edit ? 'Save changes' : 'Add cell'}
                onClose={handleCloseModal}
                show={showModal}
                onSubmit={edit ? handleUpdateCell : handleSaveCell}
            >
                <form className='grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2' onSubmit={(e) => e.preventDefault()}>
                    <div className="sm:col-span-2">
                        <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">Name</label>
                        <div className="mt-2">
                            <input
                                id="name"
                                name="name"
                                type="text"
                                autoComplete="cell name"
                                required
                                className="px-2 py-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                                value={formData?.name || ""}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                    <div className="sm:col-span-2">
                        <label htmlFor="address" className="block text-sm font-medium leading-6 text-gray-900">Address</label>
                        <div className="mt-2">
                            <input
                                id="address"
                                name="address"
                                type="text"
                                autoComplete="Cell Location"
                                required
                                className="px-2 py-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                                value={formData?.address || ""}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="city" className="block text-sm font-medium leading-6 text-gray-900">City</label>
                        <div className="mt-2">
                            <input
                                id="city"
                                name="city"
                                type="text"
                                autoComplete="address-level2"
                                list="cell-city-options"
                                className="px-2 py-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                                value={formData?.city || ""}
                                onChange={handleChange}
                            />
                            <datalist id="cell-city-options">
                                {cities.map(city => <option key={city} value={city} />)}
                            </datalist>
                        </div>
                    </div>

                    <div>
                        <label htmlFor="cell_leader" className="block text-sm font-medium leading-6 text-gray-900">Cell Leader</label>
                        <div className="mt-2">
                            <SearchableSelect
                                name="cell_leader"
                                value={formData?.cell_leader || ""}
                                onChange={handleChange}
                                placeholder="Select a cell leader"
                                options={leaderOptions}
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="meeting_day" className="block text-sm font-medium leading-6 text-gray-900">Meeting Day</label>
                        <div className="mt-2">
                            <SearchableSelect
                                name="meeting_day"
                                value={formData?.meeting_day || ""}
                                onChange={handleChange}
                                placeholder="Select an option"
                                options={[
                                    { value: 'Monday', label: 'Monday' },
                                    { value: 'Tuesday', label: 'Tuesday' },
                                    { value: 'Thursday', label: 'Thursday' },
                                    { value: 'Saturday', label: 'Saturday' },
                                ]}
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="cell_type" className="block text-sm font-medium leading-6 text-gray-900">Cell Type</label>
                        <div className="mt-2">
                            <SearchableSelect
                                name="cell_type"
                                value={formData?.cell_type || ""}
                                onChange={handleChange}
                                placeholder="Select an option"
                                options={[
                                    { value: 'Campus Cell', label: 'Campus Cell' },
                                    { value: 'Home Cell', label: 'Home Cell' },
                                    { value: 'Family Cell', label: 'Family Cell' },
                                    { value: 'inactive', label: 'Inactive Cell' },
                                ]}
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="meeting_time" className="block text-sm font-medium leading-6 text-gray-900">Meeting Time</label>
                        <div className="mt-2">
                            <input className="px-2 py-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                                autoComplete="Cell Location"
                                required
                                name="meeting_time"
                                type="time"
                                value={formData?.meeting_time || ""}
                                onChange={handleChange} />

                        </div>
                    </div>
                </form>
            </Modal>
            <main className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Cells</h1>
                        <p className="text-gray-500 mt-1">Manage your cell groups</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            onClick={handleExport}
                            disabled={filteredCells.length === 0}
                            className="flex flex-row items-center gap-2 bg-white text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50 font-semibold px-4 py-2 rounded-lg shadow-sm transition-all duration-150 disabled:opacity-50"
                            type="button"
                        >
                            <DownloadIcon className="h-5 w-5" />
                            Export to Excel
                        </button>
                        <button
                            onClick={() => handleAddCell()}
                            className="flex flex-row items-center gap-2 bg-emerald-500 text-white hover:bg-emerald-600 active:bg-emerald-700 font-semibold px-4 py-2 rounded-lg shadow-md transition-all duration-150"
                            type="button"
                        >
                            <PlusCircleIcon className="h-5 w-5" />
                            Add Cell
                        </button>
                    </div>
                </div>

                {/* Filters & view toggle */}
                {cells.length > 0 && (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                            <span className="font-medium">City:</span>
                            <select
                                value={cityFilter}
                                onChange={(e) => setCityFilter(e.target.value)}
                                className="rounded-md border-gray-300 shadow-sm focus:border-emerald-300 focus:ring focus:ring-emerald-200 focus:ring-opacity-50 text-sm"
                            >
                                <option value="">All cities ({cells.length})</option>
                                {cities.map(city => (
                                    <option key={city} value={city}>
                                        {city} ({cells.filter(c => (c.city || '').trim() === city).length})
                                    </option>
                                ))}
                                {hasCellsWithoutCity && <option value="__none__">No city set</option>}
                            </select>
                        </label>
                        <div className="inline-flex rounded-lg shadow-sm ring-1 ring-gray-300 overflow-hidden self-start" role="group" aria-label="View">
                            <button
                                type="button"
                                onClick={() => setView('table')}
                                aria-pressed={view === 'table'}
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium ${view === 'table' ? 'bg-emerald-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                            >
                                <TableIcon className="h-4 w-4" /> Table
                            </button>
                            <button
                                type="button"
                                onClick={() => setView('map')}
                                aria-pressed={view === 'map'}
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium ${view === 'map' ? 'bg-emerald-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                            >
                                <MapIcon className="h-4 w-4" /> Map
                            </button>
                        </div>
                    </div>
                )}

                {/* Stats Cards */}
                {cells.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <StatCard
                            title="Total Cells"
                            value={totalCells}
                            icon={<UserGroupIcon className="h-6 w-6 text-white" />}
                            bgColor="bg-emerald-500"
                            ringColor="ring-emerald-500"
                            active={typeFilters.length === 0}
                            onClick={() => setTypeFilters([])}
                            hint="Show all cell types"
                        />
                        <StatCard
                            title="Home Cells"
                            value={homeCells}
                            icon={<HomeIcon className="h-6 w-6 text-white" />}
                            bgColor="bg-blue-500"
                            ringColor="ring-blue-500"
                            active={typeFilters.includes('Home Cell')}
                            onClick={() => toggleType('Home Cell')}
                            hint="Show or hide home cells (you can pick several)"
                        />
                        <StatCard
                            title="Campus Cells"
                            value={campusCells}
                            icon={<UserGroupIcon className="h-6 w-6 text-white" />}
                            bgColor="bg-purple-500"
                            ringColor="ring-purple-500"
                            active={typeFilters.includes('Campus Cell')}
                            onClick={() => toggleType('Campus Cell')}
                            hint="Show or hide campus cells (you can pick several)"
                        />
                        <StatCard
                            title="Family Cells"
                            value={familyCells}
                            icon={<HomeIcon className="h-6 w-6 text-white" />}
                            bgColor="bg-orange-500"
                            ringColor="ring-orange-500"
                            active={typeFilters.includes('Family Cell')}
                            onClick={() => toggleType('Family Cell')}
                            hint="Show or hide family cells (you can pick several)"
                        />
                    </div>
                )}

                {/* Active filters */}
                {(typeFilters.length > 0 || cityFilter) && cells.length > 0 && (
                    <div className="mb-3 flex flex-wrap items-center gap-2 text-sm text-gray-700" aria-live="polite">
                        <span>Showing {filteredCells.length} of {cells.length} cells</span>
                        {cityFilter && (
                            <FilterChip label={cityFilter === '__none__' ? 'No city set' : cityFilter} onRemove={() => setCityFilter('')} />
                        )}
                        {typeFilters.map(t => (
                            <FilterChip key={t} label={t} onRemove={() => setTypeFilters(typeFilters.filter(x => x !== t))} />
                        ))}
                        <button type="button" onClick={() => setSearchParams(view === 'map' ? { view: 'map' } : {})} className="text-emerald-700 underline underline-offset-2 hover:text-emerald-900">
                            Clear filters
                        </button>
                    </div>
                )}

                {/* Table */}
                <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                    {cells.length > 0 && view === 'map' ? (
                        <CellMap cells={filteredCells} onGeocodeMissing={handleGeocodeMissing} geocoding={geocoding} onEditCell={handleEditCell} />
                    ) : cells.length > 0 ? (
                        <div className="overflow-x-auto">
                            <Table columns={columns} data={filteredCells} resetKey={`${cityFilter}|${typeFilters.join(',')}`} />
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-16">
                            <UserGroupIcon className="h-16 w-16 text-gray-300 mb-4" />
                            <h2 className="text-xl font-semibold text-gray-700">No cells found</h2>
                            <p className="text-gray-500 mt-1">Get started by adding a new cell</p>
                            <button
                                onClick={() => handleAddCell()}
                                className="mt-4 flex items-center gap-2 bg-emerald-500 text-white hover:bg-emerald-600 font-medium px-4 py-2 rounded-lg transition-colors"
                            >
                                <PlusCircleIcon className="h-5 w-5" />
                                Add Your First Cell
                            </button>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}

// Stat card that doubles as a filter toggle.
const StatCard = ({ title, value, icon, bgColor, ringColor, active, onClick, hint }) => {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            title={hint}
            className={`w-full text-left bg-white shadow-lg rounded-lg p-6 transition-shadow hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${ringColor} ${active ? `ring-2 ${ringColor}` : 'ring-0'}`}
        >
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-600 text-sm font-medium mb-1">{title}</p>
                    <p className="text-3xl font-bold text-gray-900">{value}</p>
                </div>
                <div className={`${bgColor} rounded-full p-3`}>
                    {icon}
                </div>
            </div>
        </button>
    );
};

const FilterChip = ({ label, onRemove }) => (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 font-medium text-emerald-800 ring-1 ring-emerald-200">
        {label}
        <button type="button" onClick={onRemove} aria-label={`Remove filter ${label}`} className="ml-0.5 rounded-full px-1 hover:bg-emerald-100">
            ×
        </button>
    </span>
);

Cell.propTypes = {
    cells: PropTypes.array,
    cellsGetRequested: PropTypes.bool,
    assignableLeaders: PropTypes.array,
    getCellsRequested: PropTypes.func.isRequired,
    addCellRequested: PropTypes.func.isRequired,
    deleteCellRequested: PropTypes.func.isRequired,
    updateCellRequested: PropTypes.func.isRequired,
    getAssignableLeadersRequested: PropTypes.func.isRequired
};

Cell.defaultProps = {
    cells: [],
    cellsGetRequested: false,
    assignableLeaders: []
};

const mapStateToProps = (state) => ({
    cells: state.cell.cells,
    assignableLeaders: state.cellLeader.assignableLeaders,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
    getCellsRequested,
    getAssignableLeadersRequested,
    addCellRequested,
    updateCellRequested,
    deleteCellRequested
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Cell);